/**
 * Format a token count for display, rendering an em dash for missing values.
 *
 * A `null`/`undefined` count means the turn streamed but Bedrock never
 * reported usage (e.g. the stream raised before the trailing metadata event) —
 * which is distinct from a real zero, so it must not render as `0`.
 */
export function formatTokenCount(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return "—";
  }
  // Pin the locale so thousands grouping is deterministic regardless of the
  // host environment (matters for both the admin UI and this function's test).
  return value.toLocaleString("en-US");
}

/**
 * Estimate the token count of a value, matching the service-side heuristic
 * (`CHARS_PER_TOKEN = 3` over the serialized length). Approximate by design —
 * it is only for at-a-glance per-message-part sizing in the debug view.
 */
export function estimateTokens(value: unknown): number {
  const text = typeof value === "string" ? value : JSON.stringify(value ?? "");
  return Math.floor(text.length / 3);
}

/** Format a token count in compact thousands: `0.1k`, `1.2k`, `15k`. */
export function formatTokensCompact(count: number): string {
  return `${(count / 1000).toFixed(1).replace(/\.0$/, "")}k`;
}

/**
 * Build the per-message-part token label, or `null` when it should be hidden.
 *
 * Counts below 100 are dropped — at that size the label is more noise than
 * signal. Everything shown is the compact `~x.yk tok` form.
 */
export function formatTokenLabel(count: number): string | null {
  if (count < 100) {
    return null;
  }
  return `~${formatTokensCompact(count)} tok`;
}

/**
 * Label for a compressed message part: `~4.5k -> 2.3k tok` (original size →
 * the compressed size actually sent). `null` when the original is too small
 * to be worth showing.
 */
export function formatTokenDelta(
  originalCount: number,
  sentCount: number,
): string | null {
  if (originalCount < 100) {
    return null;
  }
  return `~${formatTokensCompact(originalCount)} -> ${formatTokensCompact(sentCount)} tok`;
}
